import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATH_ROOT } from 'src/routes/paths';
import {
  Settings as SettingsIcon,
  People as PeopleIcon,
  Mail as InviteIcon,
  Security as RolesIcon,
  Extension as IntegrationsIcon,
} from '@mui/icons-material';
import { ArrowLeft } from 'lucide-react';
import { Button } from 'src/components/ui/Button';
import { StatChit, NavigationChit, InviteMembersModal } from 'src/components/organization';
import { OrganizationOverviewCard } from 'src/components/organization/OrganizationOverviewCard';
import { useDemoOrgData } from '@/hooks/organization/useDemoOrgData';
import { DEF_JAM_ORG_ID } from '@/mockData/common/demoFilters';
import {
  useOrgInvites,
  useOrgRoles,
  useOrgMembers,
  useOrganizations,
} from 'src/hooks/organization';
import { notifyDemoAction } from 'src/utils/demoNotifications';
import { useOrg } from 'src/contexts/OrgContext';
import { useDemoOrg } from 'src/contexts/DemoOrgContext';

interface OrganizationManagementProps {
  isDemo?: boolean;
}

const OrganizationManagement: React.FC<OrganizationManagementProps> = ({ isDemo = false }) => {
  const navigate = useNavigate();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const {
    getOrganizationById,
    getMembersByOrgId,
    getInvitationsByOrgId,
    getCustomRolesByOrgId,
  } = useDemoOrgData();

  const { selectedOrgId } = useOrg();
  const demoOrgContext = useDemoOrg();
  const orgId = isDemo ? demoOrgContext?.selectedOrg?.orgId : selectedOrgId;
  const activeOrgId = isDemo ? (orgId ?? DEF_JAM_ORG_ID) : orgId;

  const { organizations } = useOrganizations();

  // BUG #222 — /org/manage rendered as a blank screen when the user
  // landed without a selectedOrgId (e.g. via direct URL or mid-org-
  // switch race). The page expects an org context — without one the
  // overview card falls through to a useless "Unknown Organization"
  // stub and the nav chits link to org-scoped paths that 404. Bounce
  // back to the org list so the user picks one first.
  useEffect(() => {
    if (isDemo) return;
    if (!activeOrgId && organizations.length > 0) {
      navigate(PATH_ROOT.organization.organizations, { replace: true });
    }
  }, [isDemo, activeOrgId, organizations.length, navigate]);
  const { members } = useOrgMembers(isDemo ? undefined : activeOrgId);
  const { invites, createInvite } = useOrgInvites(isDemo ? undefined : activeOrgId);
  const { customRoles } = useOrgRoles(isDemo ? undefined : activeOrgId);

  const demoOrganization = getOrganizationById(activeOrgId ?? DEF_JAM_ORG_ID);
  const demoMembers = getMembersByOrgId(activeOrgId ?? DEF_JAM_ORG_ID);
  const demoInvites = getInvitationsByOrgId(activeOrgId ?? DEF_JAM_ORG_ID);
  const demoCustomRoles = getCustomRolesByOrgId(activeOrgId ?? DEF_JAM_ORG_ID);
  const currentOrg = !isDemo && activeOrgId
    ? organizations.find((org) => org.orgId === activeOrgId)
    : null;

  const organization = isDemo
    ? demoOrganization
    : currentOrg || getOrganizationById(activeOrgId ?? DEF_JAM_ORG_ID) || {
        orgId: activeOrgId || 'unknown-org',
        name: 'Unknown Organization',
        type: 'other' as const,
        memberCount: 0,
        primaryOwnerId: 'user-1',
        description: 'Organization not found',
        createdAt: '2024-01-01T00:00:00Z',
      };

  const handleInviteSubmit = async (
    email: string,
    roleId: string | undefined,
    customRoleId: string | undefined,
  ) => {
    if (isDemo) {
      notifyDemoAction(`Invite ${email}`, isDemo);
      return;
    }

    await createInvite(email, roleId, customRoleId);
    setShowInviteModal(false);
  };

  const stats = isDemo ? {
    totalMembers: demoMembers.length,
    activeContracts: demoOrganization?.dealCount ?? 0,
    pendingInvitations: demoInvites.filter((invite) => invite.status === 'pending').length,
    customRolesCount: demoCustomRoles.length,
  } : {
    totalMembers: members.length,
    activeContracts: 0,
    pendingInvitations: invites.filter((invite) => invite.status === 'pending').length,
    customRolesCount: customRoles.length,
  };

  const navigationItems = [
    {
      title: 'Organization Settings',
      description: 'Manage organization details, billing, and preferences',
      icon: <SettingsIcon />,
      path: isDemo ? PATH_ROOT.demo.organizationSettings(activeOrgId || '') : PATH_ROOT.organization.settings,
    },
    {
      title: 'Members',
      description: `Manage ${stats.totalMembers} team member${stats.totalMembers !== 1 ? 's' : ''} and their roles`,
      icon: <PeopleIcon />,
      path: isDemo ? PATH_ROOT.demo.organizationMembers(activeOrgId || '') : PATH_ROOT.organization.members,
    },
    {
      title: 'Invitations',
      description: `${stats.pendingInvitations} pending invitation${stats.pendingInvitations !== 1 ? 's' : ''} awaiting response`,
      icon: <InviteIcon />,
      path: isDemo ? PATH_ROOT.demo.organizationInvitations(activeOrgId || '') : PATH_ROOT.organization.invitations,
    },
    {
      title: 'Custom Roles',
      description: `${stats.customRolesCount} custom role${stats.customRolesCount !== 1 ? 's' : ''} with specific permissions`,
      icon: <RolesIcon />,
      path: isDemo ? PATH_ROOT.demo.organizationRoles(activeOrgId || '') : PATH_ROOT.organization.roles,
    },
    {
      title: 'Integrations',
      description: 'Connect email, calendar, and other tools to your workspace',
      icon: <IntegrationsIcon />,
      path: isDemo ? PATH_ROOT.demo.organizationIntegrations(activeOrgId || '') : PATH_ROOT.organization.integrations,
    },
  ];

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="mb-8">
        <button
          onClick={() => navigate(isDemo ? PATH_ROOT.demo.organizations : PATH_ROOT.organization.organizations)}
          className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        {organization && (
          <OrganizationOverviewCard
            organization={organization}
            size="large"
          />
        )}
      </div>

      <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2">
        <StatChit value={stats.activeContracts} label="Active Contracts" />
        <StatChit value={stats.totalMembers} label="Team Members" />
      </div>

      <div className="mb-8 space-y-4">
        {navigationItems.map((item) => (
          <NavigationChit
            key={item.title}
            title={item.title}
            description={item.description}
            icon={item.icon}
            onClick={() => navigate(item.path)}
          />
        ))}
      </div>

      <div className="mt-8 flex gap-3">
        <Button
          variant="outline"
          size="lg"
          onClick={() => setShowInviteModal(true)}
        >
          Invite Members
        </Button>
        <Button
          variant="ghost"
          size="lg"
          onClick={() => navigate(isDemo ? PATH_ROOT.demo.organizationSettings(activeOrgId || '') : PATH_ROOT.organization.settings)}
        >
          Organization Settings
        </Button>
      </div>

      {showInviteModal && (
        <InviteMembersModal
          isDemo={isDemo}
          orgId={activeOrgId}
          customRoles={isDemo ? demoCustomRoles : customRoles}
          onClose={() => setShowInviteModal(false)}
          onSubmit={handleInviteSubmit}
        />
      )}
    </div>
  );
};

export default OrganizationManagement;
